import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  FormLabel,
  Grid,
  TextField,
  Theme,
} from "@mui/material";
import Alert from "@mui/material/Alert";
import { useTheme } from "@mui/material/styles";
import Field from "components/Field";
import SelectApplication from "components/SelectApplication";
import SelectEnvironment from "components/SelectEnvironment";
import arrayMutators from "final-form-arrays";
import { pick } from "lodash";
import { useMemo } from "react";
import { FormProps, withTypes } from "react-final-form";
import { FormattedMessage as T, useIntl } from "react-intl";
import {
  IOrganizationApplicationDomain,
  OrganizationApplicationDomainSaveRequest,
} from "services/KnowfullyAdminClient";

const useStyles = (theme: Theme) => ({
  feedback: {
    width: "100%",
    margin: "20px 0 20px 0",
  },
  subtitle: {
    margin: theme.spacing(3, 0, 2),
    color: theme.palette.primary.main,
    fontWeight: "bold",
  },
  checkboxContainer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  lineContainer: {
    display: "flex",
    alignItems: "center",
  },
});

const { Form } = withTypes<OrganizationApplicationDomainSaveRequest>();

type OrganizationApplicationDomainFormProps = {
  organizationApplicationDomain?: IOrganizationApplicationDomain | null;
  title: string;
  onCancel?: () => void;
  onSubmit: (saveRequest: OrganizationApplicationDomainSaveRequest) => void;
};

const OrganizationApplicationDomainForm = ({
  organizationApplicationDomain,
  title,
  onCancel,
  onSubmit,
}: OrganizationApplicationDomainFormProps &
  FormProps<OrganizationApplicationDomainSaveRequest>) => {
  const { formatMessage: t } = useIntl();
  const styles = useStyles(useTheme());

  const isEdit = !!organizationApplicationDomain;

  const init = useMemo(
    () => ({
      ...pick(organizationApplicationDomain ?? {}, [
        "id",
        "applicationId",
        "organizationApplicationId",
        "hostName",
        "environment",
        "isDefault",
      ]),
    }),
    [organizationApplicationDomain]
  );

  return (
    <Form
      onSubmit={onSubmit}
      initialValues={init}
      mutators={{ ...arrayMutators }}
      render={({ handleSubmit, submitting, submitError, values }) => (
        <form onSubmit={handleSubmit}>
          <DialogTitle id="form-dialog-title">{title}</DialogTitle>
          <DialogContent dividers>
            <Grid container spacing={2}>
              <Field name="applicationId" required>
                {({ input, meta }) => (
                  <Grid item xs={12}>
                    <SelectApplication
                      {...input}
                      value={values.applicationId}
                      disabled={isEdit}
                      label={t({ id: "applicationLabel" })}
                      textFieldProps={{
                        error: meta.error && meta.submitFailed,
                        required: true,
                      }}
                      required
                    />
                  </Grid>
                )}
              </Field>

              <Field name="hostName" required>
                {({ input, meta }) => (
                  <Grid item xs={12}>
                    <TextField
                      variant="standard"
                      fullWidth
                      required
                      label={t({
                        id: "hostNameLabel",
                      })}
                      error={meta.error && meta.submitFailed}
                      helperText={meta.error && meta.submitFailed && meta.error}
                      {...input}
                    />
                  </Grid>
                )}
              </Field>

              <Field name="environment" required>
                {({ input, meta }) => (
                  <Grid item xs={12}>
                    <FormControl
                      fullWidth
                      required
                      variant="standard"
                      error={meta.error && meta.submitFailed}
                    >
                      <SelectEnvironment {...input} />
                      {meta.error && meta.submitFailed && (
                        <FormHelperText>{meta.error}</FormHelperText>
                      )}
                    </FormControl>
                  </Grid>
                )}
              </Field>

              <Field name="isDefault" type="checkbox">
                {({ input }) => (
                  <Grid item xs={12}>
                    <Box sx={styles.lineContainer}>
                      <FormLabel>
                        <T id="isDefaultLabel" />
                      </FormLabel>
                      <Checkbox {...input} />
                    </Box>
                  </Grid>
                )}
              </Field>

              {submitError && (
                <Alert sx={styles.feedback} severity="error">
                  {submitError}
                </Alert>
              )}
            </Grid>
          </DialogContent>

          <DialogActions>
            {submitting && <CircularProgress size={24} />}
            <Button disabled={submitting} onClick={onCancel}>
              {t({ id: "cancelButton" })}
            </Button>

            <Button type="submit" color="primary" disabled={submitting}>
              {t({ id: "saveButton" })}
            </Button>
          </DialogActions>
        </form>
      )}
    />
  );
};

export default OrganizationApplicationDomainForm;
